import React, { useState, useEffect } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { Link } from "react-router-dom";
import "./WorkSpeaks.css";
import { Helmet } from 'react-helmet';
import { getPreloadedData, PreloadedScript } from "../../utils/preloadedData";

// Helper function to create a URL-friendly slug from the title
const createSlug = (title) => {
  if (!title) return "";
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
};

function WorkSpeaks({ scrollToAppointment }) {
  const [selectedCategory, setSelectedCategory] = useState("All");
  // Seeded from the prerendered snapshot so the first client render matches the static HTML.
  const [portfolioItems, setPortfolioItems] = useState(() => getPreloadedData("__PRELOADED_PORTFOLIO_LIST__") || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(() => getPreloadedData("__PRELOADED_PORTFOLIO_LIST__") === null);
  const postsPerPage = 6;

  const getImageUrl = (imageUrl) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    if (!imageUrl || typeof imageUrl !== 'string' || imageUrl === '{}') {
      return `${baseUrl}/images/default-image.jpg`;
    }
    return `${baseUrl}${imageUrl.startsWith("/") ? imageUrl : "/" + imageUrl}`;
  };

  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        const response = await axiosInstance.get("api/portfolios");
        const sortedPortfolioItems = (Array.isArray(response.data) ? response.data : []).sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        );
        setPortfolioItems(sortedPortfolioItems);
      } catch (error) {
        console.error("Error fetching portfolio items:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolios();
  }, []);

  const filteredItems = selectedCategory === "All"
    ? portfolioItems
    : portfolioItems.filter((item) =>
        item.portfolio_category && item.portfolio_category.includes(selectedCategory)
      );

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredItems.length / postsPerPage);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo(0, 0);
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    );
  }

  return (
    <div className="main_div_work_speaks">
      <PreloadedScript id="__PRELOADED_PORTFOLIO_LIST__" data={portfolioItems} />
      <Helmet>

        <meta property="og:title" content="Portfolio: Software, AI & Cloud Projects | 360 SynergyTech" />
        <meta property="og:url" content="https://360synergytech.com/portfolio" />
        <meta property="og:image" content="https://360synergytech.com/synergy-logo.png" />
        <meta property="og:description" content="Discover our portfolio of successful projects across AI, cloud, software engineering, and digital transformation at 360 SynergyTech." />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Portfolio: Software, AI & Cloud Projects | 360 SynergyTech" />
        <meta name="twitter:description" content="Explore projects and digital solutions delivered by SynergyTech." />
        <meta name="twitter:image" content="https://360synergytech.com/synergy-logo.png" />

        
        <title>Portfolio: Software, AI & Cloud Projects | 360 SynergyTech</title>
        <meta name="description" content="See our success stories at 360 SynergyTech, from custom software to AI integrations, we’ve delivered innovative, scalable solutions that drive real business impact" />
        <link rel="canonical" href="https://360synergytech.com/portfolio" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "name": "360 SynergyTech Portfolio",
              "url": "https://360synergytech.com/portfolio",
              "description": "Explore projects and case studies delivered by 360 SynergyTech in AI, web, and cloud solutions."
            }
          `}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://360synergytech.com/" },
              { "@type": "ListItem", position: 2, name: "Portfolio", item: "https://360synergytech.com/portfolio" },
            ],
          })}
        </script>
      </Helmet>
      <div className="row m-0 p-0">
        <div className="col-lg-2 col-md-1 col-sm-12 col-12"></div>
        <div className="col-lg-8 col-md-10 col-sm-12 col-12">
          <div className="row m-0 p-0 align-items-center">
            <div className="col-lg-10 col-md-9 col-sm-9 col-12">
              <h1 className="heading_work_speaks">
              AI, Cloud & Software by 360 SynergyTech
              </h1>
              <h2 className="heading_work_speaks_para">
                Discover our proven track record in delivering innovative, scalable, and high-performance digital solutions. From custom software to AI-driven platforms, explore case studies that showcase measurable results, user-focused design, and business growth success.
              </h2>            
            </div>
            <div className="col-lg-2 col-md-3 col-sm-3 col-12 text-center mb-3">
              <button className="btn_lets_talk" type="button" onClick={scrollToAppointment}>
                Let's Talk
              </button>
            </div>
          </div>

          <div className="main_btns_stacks">
            <div className="row m-0 p-0">
              {["Web Development", "CRMS", "UI UX Design", "Marketing", "All"].map((category) => (
                <div className="col-lg col-md col-sm-6 col-6" key={category}>
                  <div className="d-flex justify-content-center">
                    <button
                      className={`btn_web_developement ${selectedCategory === category ? 'btn_web_developement_selected' : ''}`}
                      onClick={() => {
                        setSelectedCategory(category);
                        setCurrentPage(1);
                      }}
                    >
                      {category}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="row">
            {currentItems.map((item) => (
              <div key={item.id} className="col-lg-6 col-md-6 col-sm-12 col-12">
                <div className="card_case_study">
                  <img
                    className="card_image"
                    src={getImageUrl(item.image)}
                    alt={item.title}
                    width="600"
                    height="330"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `${process.env.REACT_APP_BASE_URL}/images/default-image.jpg`;
                    }}
                  />
                  <div className="card_hover_content">
                    <div className="row m-0 p-0">
                      <div className="col-lg-8 col-md-8 col-sm-8 col-8">
                        <h3 className="heading_card_usecase">{truncateText(item.title, 25)}</h3>
                        <p className="para_card_usecase">
                          {truncateText(item.description, 25)}
                        </p>
                      </div>
                      <div className="col-lg-4 col-md-4 col-sm-4 col-4">
                        <Link to={`/portfolio/detail/${item.id}/${createSlug(item.title)}`}>
                          <button className="read_more_btn">Read more</button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination_div" style={paginationStyles.container}>
              {[...Array(totalPages)].map((_, index) => {
                const pageNumber = index + 1;
                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    style={
                      currentPage === pageNumber
                        ? { ...paginationStyles.dot, ...paginationStyles.activeDot }
                        : paginationStyles.dot
                    }
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="col-lg-2 col-md-1 col-sm-12 col-12"></div>
      </div>
    </div>
  );
}

const paginationStyles = {
  container: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
    marginBottom: "20px",
  },
  dot: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#e0e0e0",
    margin: "0 5px",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    color: "#333",
    transition: "background-color 0.3s",
  },
  activeDot: {
    backgroundColor: "#ff6200",
    color: "#fff",
  },
};

export default WorkSpeaks;